import './currencies-view.css';

import { Button } from '@kerfjs/ui/button';
import { ContentItem } from '@kerfjs/ui/content-item';
import { px } from '@kerfjs/ui/css-values';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { ListItem } from '@kerfjs/ui/list-item';
import { SearchField } from '@kerfjs/ui/search-field';
import { Spacer } from '@kerfjs/ui/spacer';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import type { SafeHtml } from 'kerfjs';

import { effectiveFxRates } from '../../../shared/fx-rates.js';
import { currencyEditCode, currencyPickerFilter, currencyPickerOpen } from '../../data/state.js';
import { currencyChoices, filterCurrencyChoices, formatFxDisplayRate } from '../../lib/currencies.js';

/** Organization exchange rates, edited one at a time so each change saves immediately. */
export function CurrenciesView({
  assumptions,
  baseCurrency,
  currencies,
}: {
  assumptions: Record<string, unknown>;
  baseCurrency: string;
  currencies: readonly string[];
}): SafeHtml {
  const rates = effectiveFxRates(assumptions);
  const choices = currencyChoices(baseCurrency, currencies);
  const filtered = filterCurrencyChoices(choices, currencyPickerFilter.value);
  const editing = currencyEditCode.value;
  return (
    <List gap="xs" controlInsets="tb">
      <Text tone="quiet" size="compact" controlMargins="rl">
        Exchange rates convert foreign prices to {baseCurrency}. Select a currency to edit its rate.
      </Text>
      {currencies.length === 0 ? (
        <EmptyState title="No Currencies Yet" detail="Add a currency to set an exchange rate." />
      ) : (
        <Grid minColumnWidth={px(240)} autoFill gap={{ column: 'none', row: 's' }} ariaLabel="Configured Currencies">
          {currencies.map((code) => {
            const rate = rates[code];
            return (
              <ContentItem
                interactive
                action="currency-edit-open"
                itemId={code}
                frame="framed"
                title={`Edit ${code} Exchange Rate…`}
                ariaLabel={`Edit ${code} Exchange Rate…`}
                rootAttributes={{ 'data-key': code, 'data-currency': code }}
                appearance="surface"
              >
                <List>
                  <ListHeader label={`${code} ⭤ ${baseCurrency}`} />
                  <List controlInsets="rl">
                    <Text>
                      1 {code} = {formatFxDisplayRate(rate)} {baseCurrency}
                    </Text>
                    <Text>
                      1 {baseCurrency} = {formatFxDisplayRate(rate === undefined ? undefined : 1 / rate)} {code}
                    </Text>
                  </List>
                </List>
              </ContentItem>
            );
          })}
        </Grid>
      )}
      {currencyPickerOpen.value ? (
        <DialogSurface
          label="Add a Currency"
          lightDismiss
          rootAttributes={{
            'data-action': 'currency-picker-dialog',
            'data-key': 'currency-picker',
            'data-auto-open-dialog': '',
          }}
          footer={
            <Button emphasis="plain" action="currency-picker-cancel" rootAttributes={{ 'data-dialog-close': '' }}>
              Cancel
            </Button>
          }
        >
          <List gap="xs">
            <ListInsetControl>
              <SearchField
                id="currency-filter"
                label="Search Currencies"
                presentation="form-field"
                query={currencyPickerFilter.value}
                clearAction="currency-filter-clear"
                clearLabel="Clear Currency Search"
              />
            </ListInsetControl>
            <div class="currency-picker-results">
              {filtered.length === 0 ? (
                <EmptyState title="No Matching Currencies" detail="Try another search." />
              ) : (
                <List>
                  {filtered.map((choice) => (
                    <ListItem
                      action="currency-select"
                      itemId={choice.value}
                      label={choice.label}
                      status={choice.unavailable}
                      disabled={choice.unavailable !== undefined}
                      multiline
                      rootAttributes={{ 'data-currency': choice.value }}
                    />
                  ))}
                </List>
              )}
            </div>
          </List>
        </DialogSurface>
      ) : null}
      {editing !== null ? (
        <DialogSurface
          label={`${editing} Exchange Rate`}
          lightDismiss
          rootAttributes={{
            'data-action': 'currency-edit-dialog',
            'data-key': 'currency-edit',
            'data-auto-open-dialog': '',
          }}
          footer={
            <>
              <Button emphasis="plain" action="currency-edit-cancel" rootAttributes={{ 'data-dialog-close': '' }}>
                Cancel
              </Button>
              <Spacer flex />
              {rates[editing] !== undefined ? (
                <Button emphasis="plain" action="currency-remove">
                  Remove Currency
                </Button>
              ) : null}
              <Button tone="brand" emphasis="primary" action="currency-save">
                Save Rate
              </Button>
            </>
          }
        >
          <ListInsetControl>
            <TextField
              name="currency-rate"
              label={`1 ${editing} in ${baseCurrency}`}
              type="number"
              min="0"
              step="any"
              value={String(rates[editing] ?? '')}
            />
          </ListInsetControl>
        </DialogSurface>
      ) : null}
    </List>
  );
}
